import { useEffect, useRef } from 'react'
import { RSAAResultAction } from 'redux-api-middleware'
import { useParams } from 'react-router-dom'
import {
  useAppSelector,
  useAppDispatch,
  useUrlSearchParams,
} from '../../../hooks'
import {
  ThermostatBrandName,
  findBrandByPortal,
} from '../../../types/thermostatBrands'
import BroadcastChannelName from '../../../types/broadcastChannelName'
import { BroadcastChannel } from 'broadcast-channel'
import { thermostatAuthSelector } from '../../../reducers/thermostatAuth'
import thermostatAuth from '../../../actions/thermostatAuth'
import {
  postMessagetoMobileIfPresent,
  CONNECT_THERMOSTAT_CLOSE_TYPE,
} from '../../../utils/messageUtils'
import { ConnectionStatusMode } from '../DeviceConnectionRedirect'
import data from './resources/data'

export interface ThermostatRedirectBroadcastMessage {
  success: boolean
  brand: ThermostatBrandName
}

// Long enough to read the success state, short enough that nobody reaches for the
// button. The button is the "go back now" affordance, not the requirement.
const AUTO_CLOSE_DELAY_MS = 3000

async function sendAuthResultToApp(
  success: boolean,
  brand: ThermostatBrandName,
) {
  const channel = new BroadcastChannel(BroadcastChannelName.ThermostatRedirect)
  // `postMessage` resolves once the message has actually been handed off. We close
  // this tab on success, so awaiting it is what stops us from tearing the tab down
  // mid-delivery and leaving the app tab sat on the permissions step forever.
  await channel.postMessage({ success, brand })
}

export interface RouteParams {
  brand: ThermostatBrandName
}

enum RedirectUrlParams {
  code = 'code',
  state = 'state',
  externallyAuthenticating = 'externally_authenticating',
}

function connectionStatusMode(
  authIsLoading: boolean,
  connectionSuccess: boolean,
  externalAuthInProgress: boolean,
) {
  if (authIsLoading || externalAuthInProgress) {
    return ConnectionStatusMode.Connecting
  } else if (connectionSuccess) {
    return ConnectionStatusMode.Connected
  }
  return ConnectionStatusMode.Failed
}

function useViewModel() {
  const dispatch = useAppDispatch()
  const { brand } = useParams<RouteParams>()
  const {
    isLoading,
    errors,
    success: authSuccess,
  } = useAppSelector(thermostatAuthSelector(brand)) ?? {
    isLoading: false,
    errors: null,
    success: null,
  }
  const urlParams = useUrlSearchParams()
  const externalAuthInProgress = urlParams.has(
    RedirectUrlParams.externallyAuthenticating,
  )

  const isMobile = 'ReactNativeWebView' in window
  const autoCloseTimeout = useRef<ReturnType<typeof setTimeout>>()

  const handleClose = () => {
    if (isMobile) {
      postMessagetoMobileIfPresent(CONNECT_THERMOSTAT_CLOSE_TYPE)
    } else {
      window.top?.close()
    }
  }

  function submitThermostatAuthentication(code: string, redirectUri: string) {
    if (isLoading || Object.keys(errors ?? {}).length || authSuccess !== null) {
      return
    }
    dispatch(
      thermostatAuth(brand).actions.authenticate(code, redirectUri),
    ).then(async (responseData: RSAAResultAction) => {
      const { type } = responseData
      const success = type === thermostatAuth(brand).types.Post.Success
      await sendAuthResultToApp(success, brand)
      // Only send ourselves away once the app tab has the result, and only on
      // success — a failure has to stay on screen so it can be read and retried.
      // Mobile is untouched: there is no second tab to get back to.
      if (success && !isMobile) {
        autoCloseTimeout.current = setTimeout(handleClose, AUTO_CLOSE_DELAY_MS)
      }
    })
  }

  useEffect(() => {
    if (urlParams.has(RedirectUrlParams.code)) {
      submitThermostatAuthentication(
        urlParams.get(RedirectUrlParams.code) as string,
        // Note we use the state to store the redirect uri, which differs based on mobile platform
        urlParams.get(RedirectUrlParams.state) as string,
      )
    }
  }, [dispatch, isMobile, urlParams])

  useEffect(() => () => clearTimeout(autoCloseTimeout.current), [])

  const codePresent = urlParams.has(RedirectUrlParams.code)
  // if the code is present in the url, assume an auth request will be made
  // otherwise, the auth request could never be loading
  const authIsLoading = codePresent && (isLoading || authSuccess === null)

  const mode = connectionStatusMode(
    authIsLoading,
    authSuccess ?? false,
    externalAuthInProgress,
  )

  return {
    mode: mode,
    // The route param is the lowercase brand id, so render the brand's display
    // name instead of "you successfully connected your nest".
    statusText: data.statusText(findBrandByPortal(brand)?.name ?? brand),
    // Mobile closes through the native bridge rather than coming back to a tab.
    statusSubText: isMobile ? undefined : data.statusSubText,
    buttonAction: handleClose,
  }
}

export default useViewModel
